import { useRef, useState } from 'react';
import { PutObjectCommand } from '@aws-sdk/client-s3';
import { config } from './config';
import { getS3 } from './aws';
import { useAuth } from './auth';
import { buildCaptionKey } from './uploads';

function isWellFormedVtt(text: string): boolean {
  return text.trimStart().startsWith('WEBVTT');
}

function isWellFormedSrt(text: string): boolean {
  return /\d{2}:\d{2}:\d{2},\d{3}\s*-->\s*\d{2}:\d{2}:\d{2},\d{3}/.test(text);
}

/**
 * Browsers only render WebVTT `<track>` files, not SRT — converting here keeps the captions
 * bucket and the player's `<track>` element VTT-only, so nothing downstream needs to know an
 * owner ever uploaded an SRT file at all.
 */
export function convertSrtToVtt(text: string): string {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const withoutSequenceNumbers = normalized.replace(
    /^\d+\n(?=\d{2}:\d{2}:\d{2},\d{3}\s*-->)/gm,
    '',
  );
  const withDotTimestamps = withoutSequenceNumbers.replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2');
  return `WEBVTT\n\n${withDotTimestamps.trim()}\n`;
}

/**
 * Lets someone add or replace a video's caption track, whether or not one was already
 * auto-generated. Direct-PUT-then-confirm flow, same shape as ThumbnailPicker: the browser
 * uploads the file straight to S3, then a single API call (`setCaption` for an owner's upload,
 * `setSharedCaption` for a shared video) records the resulting URL against the video.
 */
export function CaptionPicker({
  subjectId,
  onSave,
  onCancel,
}: {
  /** An uploadId or a shared-library videoId — only used to namespace the uploaded object's key. */
  subjectId: string;
  /** Persists the captured caption URL (calling `setCaption` or `setSharedCaption`) and handles
   * any resulting UI transition — on success, this component doesn't need to do anything further
   * itself. */
  onSave: (captionUrl: string) => Promise<void>;
  onCancel: () => void;
}) {
  const { identityId } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    const file = inputRef.current?.files?.[0];
    if (!file || !identityId) return;
    setSaving(true);
    setError(null);
    try {
      const lower = file.name.toLowerCase();
      const text = await file.text();
      let vtt: string;
      if (lower.endsWith('.vtt')) {
        if (!isWellFormedVtt(text)) throw new Error('invalid vtt');
        vtt = text;
      } else if (lower.endsWith('.srt')) {
        if (!isWellFormedSrt(text)) throw new Error('invalid srt');
        vtt = convertSrtToVtt(text);
      } else {
        throw new Error('unsupported extension');
      }
      const key = buildCaptionKey(identityId, subjectId);
      await getS3().send(
        new PutObjectCommand({
          Bucket: config.captionsBucket,
          Key: key,
          Body: vtt,
          ContentType: 'text/vtt',
        }),
      );
      const captionUrl = `https://${config.spaDomain}/${key}`;
      await onSave(captionUrl);
    } catch {
      setError("Couldn't set captions — make sure the file is a valid .vtt or .srt file.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
      <h2>Upload captions</h2>
      <p className="muted">Choose a WebVTT (.vtt) or SubRip (.srt) caption file.</p>
      <input ref={inputRef} type="file" accept=".vtt,.srt" disabled={saving} />
      <div style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 8 }}>
        <button onClick={confirm} disabled={saving}>
          {saving ? 'Uploading…' : 'Upload captions'}
        </button>
        <button type="button" className="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </div>
  );
}
